/**
 * SoftphoneDevTools Storage - Persistence layer for debug history
 *
 * Uses localforage to persist debug history across sessions.
 */

import localforage from 'localforage';
import { SOFTPHONE_DEBUG_HISTORY_KEY, MAX_DEBUG_HISTORY_CALLS } from 'Modules/softphone/components/SoftphoneDevTools/constants';
import type { CallDebugInfo } from 'Modules/softphone/services/DebugService';

/**
 * Load persisted debug history from localforage
 *
 * @returns Array of CallDebugInfo, or empty array if none found
 */
export const loadDebugHistory = async (): Promise<CallDebugInfo[]> => {
  try {
    const stored = await localforage.getItem<CallDebugInfo[]>(
      SOFTPHONE_DEBUG_HISTORY_KEY
    );

    if (!stored || !Array.isArray(stored)) {
      return [];
    }

    // Newest last, so the cap is taken from the end — see mergeDebugHistory.
    return stored.slice(-MAX_DEBUG_HISTORY_CALLS);
  } catch (error) {
    console.error('[SoftphoneDevTools] Failed to load debug history:', error);
    return [];
  }
};

/**
 * Save debug history to localforage
 *
 * @param history - Array of CallDebugInfo to persist
 */
export const saveDebugHistory = async (
  history: CallDebugInfo[]
): Promise<void> => {
  try {
    // Newest last, so the cap is taken from the end — see mergeDebugHistory.
    const trimmedHistory = history.slice(-MAX_DEBUG_HISTORY_CALLS);
    await localforage.setItem(SOFTPHONE_DEBUG_HISTORY_KEY, trimmedHistory);
  } catch (error) {
    console.error('[SoftphoneDevTools] Failed to save debug history:', error);
  }
};

/**
 * Clear persisted debug history from localforage
 */
export const clearDebugHistory = async (): Promise<void> => {
  try {
    await localforage.removeItem(SOFTPHONE_DEBUG_HISTORY_KEY);
  } catch (error) {
    console.error('[SoftphoneDevTools] Failed to clear debug history:', error);
  }
};
